// activeSafetyForms/safetyAuditForm/actions.js

import { SafetyAuditChecklist } from "../../../models/SafetyForms/safetyAudit/SafetyAudit";
import {
  createSafetyAuditChecklistFormDraft,
  getSafetyAuditChecklistFormInfo,
} from "../../../api/SafetyFormApi";
import { activeSafetyAuditFormInitialState } from "./activeSafetyFormsSafetyAuditFormState";
import { safetyAuditFormInitialState } from "../../forms/safetyAuditForm/safetyAuditFormState";
import { makeSafetyAuditFormActionsForTab } from "../../forms/safetyAuditForm/safetyAuditFormActions";

export const makeActiveSafetyAuditFormActions = (set, get) => ({
  // lifecycle
  startNewSafetyAudit: async (user) => {
    // Build local model immediately for UX
    const safetyAuditChecklistModel = new SafetyAuditChecklist();

    // Create on server to get a persistent safety_audit_checklist_id
    try {
      const response = await createSafetyAuditChecklistFormDraft(user);
      const { safety_audit_checklist_id, creation_date } = response.data || {};
      if (safety_audit_checklist_id) {
        safetyAuditChecklistModel.updateSafetyAuditChecklistId(
          safety_audit_checklist_id
        );
        safetyAuditChecklistModel.created_at = creation_date;
      }
    } catch (e) {
      // If create failed, proceed with local model; first autosave will create
    }
    set((s) => ({
      activeSafetyAuditForm: {
        ...s.activeSafetyAuditForm,
        currentSafetyAudit: safetyAuditChecklistModel,
        safetyAuditMode: "new",
        safetyAuditErrors: null,
        rev: 0,
        safetyAuditForm: safetyAuditFormInitialState,
      },
    }));
    // Upsert minimal row into Safety Audit Checklist list
    try {
      const row = {
        id: safetyAuditChecklistModel.id,
        creation_date:
          safetyAuditChecklistModel.created_at ||
          new Date().toISOString().split("T")[0],
        updated_date: new Date().toISOString().split("T")[0],
        audit_date: safetyAuditChecklistModel.audit_date || "",
        audit_time: safetyAuditChecklistModel.audit_time || "",
        project_number: safetyAuditChecklistModel.project_number || "",
        supervisor: safetyAuditChecklistModel.supervisor || "",
      };
      get().safetyFormListsActions.upsertSafetyAuditDraftLocal(row);
    } catch (err) {
      console.error("Error upserting safety audit checklist to list:", err);
    }
  },

  // Fetching a safety audit checklist form
  fetchSafetyAudit: async (safetyAuditMode, safetyAuditId) => {
    set((s) => ({
      activeSafetyAuditForm: {
        ...s.activeSafetyAuditForm,
        isLoading: true,
        error: null,
      },
    }));

    try {
      const response = await getSafetyAuditChecklistFormInfo(safetyAuditId);
      if (!response.data || response.data === false) {
        set((s) => ({
          activeSafetyAuditForm: {
            ...s.activeSafetyAuditForm,
            isLoading: false,
            error: "Safety Audit Checklist not found or could not be loaded.",
            currentSafetyAudit: null,
            safetyAuditMode: null,
            rev: 0,
          },
        }));
        return {
          ok: false,
          error: "Safety Audit Checklist not found or could not be loaded.",
        };
      }
      const safetyAuditChecklistModel = new SafetyAuditChecklist(response.data);
      const effectiveMode =
        safetyAuditMode === "copy" ? "edit" : safetyAuditMode;
      set((s) => ({
        activeSafetyAuditForm: {
          ...s.activeSafetyAuditForm,
          isLoading: false,
          currentSafetyAudit: safetyAuditChecklistModel,
          safetyAuditMode: effectiveMode,
          safetyAuditErrors: null,
          rev: 0,
          safetyAuditForm: safetyAuditFormInitialState,
        },
      }));
      // If this was a copy, ensure the new Safety Audit Checklist appears in the list immediately
      if (safetyAuditMode === "copy") {
        try {
          const row = {
            id: safetyAuditChecklistModel.id,
            creation_date:
              safetyAuditChecklistModel.created_at ||
              new Date().toISOString().split("T")[0],
            updated_date: new Date().toISOString().split("T")[0],
            audit_date: safetyAuditChecklistModel.audit_date || "",
            audit_time: safetyAuditChecklistModel.audit_time || "",
            project_number: safetyAuditChecklistModel.project_number || "",
            supervisor: safetyAuditChecklistModel.supervisor || "",
          };
          get().safetyFormListsActions.upsertSafetyAuditDraftLocal(row);
        } catch (err) {
          console.error("Error upserting copied safety audit checklist:", err);
        }
      }
      return { ok: true };
    } catch (err) {
      set((s) => ({
        activeSafetyAuditForm: {
          ...s.activeSafetyAuditForm,
          isLoading: false,
          error: err,
          currentSafetyAudit: null,
          safetyAuditMode: null,
          rev: 0,
        },
      }));
      return { ok: false, error: err };
    }
  },

  // Hydrate the active Safety Audit Checklist directly from a payload (e.g., copy/new endpoints)
  hydrateSafetyAuditFromPayload: (safetyAuditMode, safetyAuditPayload) => {
    try {
      const safetyAuditChecklistModel = new SafetyAuditChecklist(
        safetyAuditPayload
      );
      const effectiveMode =
        safetyAuditMode === "copy" ? "edit" : safetyAuditMode;
      set((s) => ({
        activeSafetyAuditForm: {
          ...s.activeSafetyAuditForm,
          currentSafetyAudit: safetyAuditChecklistModel,
          safetyAuditMode: effectiveMode,
          safetyAuditErrors: null,
          rev: 0,
          safetyAuditForm: safetyAuditFormInitialState,
        },
      }));
      return { ok: true, id: safetyAuditChecklistModel.id };
    } catch (err) {
      set((s) => ({
        activeSafetyAuditForm: {
          ...s.activeSafetyAuditForm,
          isLoading: false,
          error: err,
        },
      }));
      return { ok: false, error: err };
    }
  },

  clearSafetyAudit: () =>
    set((s) => ({
      activeSafetyAuditForm: {
        ...activeSafetyAuditFormInitialState,
        // keep tab identity
        tabName: "safetyAuditForm",
        rev: 0,
      },
    })),

  // UI helpers
  setSafetyAuditErrors: (errors) =>
    set((s) => ({
      activeSafetyAuditForm: {
        ...s.activeSafetyAuditForm,
        safetyAuditErrors: errors,
      },
    })),

  safetyAuditFormActions: Object.freeze(
    makeSafetyAuditFormActionsForTab(set, get, "activeSafetyAuditForm")
  ),
});
